import { useEffect, useMemo, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, FileXls, Wallet, TrendUp } from "@phosphor-icons/react";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const now = new Date();

export default function CashBook() {
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [dest, setDest] = useState("all"); // all | SM | KP
  const [data, setData] = useState({ days: [], totals: {} });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    const destQs = dest !== "all" ? `&cash_destination=${dest}` : "";
    api.get(`/reports/daily-cashbook?month=${month}${destQs}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat buku kas"))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, dest]);

  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      opts.push({ key, label: `${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}` });
    }
    return opts;
  }, []);

  const downloadExcel = async () => {
    try {
      const destQs = dest !== "all" ? `&cash_destination=${dest}` : "";
      const r = await api.get(`/reports/daily-cashbook.xlsx?month=${month}${destQs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `buku-kas-${dest === "all" ? "gabung" : dest}-${month}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel diunduh");
    } catch { toast.error("Gagal mengunduh Excel"); }
  };

  const t = data.totals || {};

  return (
    <div>
      <PageHeader title="Buku Kas Harian" subtitle="Arus kas showroom per hari — pemasukan (cicilan, DP, cash sale) vs pengeluaran (pembelian, komisi, biaya mobil)"
        actions={
          <Button onClick={downloadExcel} data-testid="cashbook-export-excel" className="rounded-none bg-[#10B981] hover:bg-[#0d966b] text-white uppercase tracking-wider text-xs h-10">
            <FileXls size={14} className="mr-2" weight="bold" /> Export Excel
          </Button>
        }
      />

      <div className="p-4 md:p-6 lg:p-10">
        {/* Month + destination selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Bulan</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="cashbook-month" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {monthOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-[10px] uppercase tracking-widest text-gray-500">Kas Tujuan</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {[
                { v: "all", label: "Semua Kas", color: "gray" },
                { v: "SM", label: "Kas Showroom (SM)", color: "black" },
                { v: "KP", label: "Kas Kantor Pusat (KP)", color: "orange" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setDest(opt.v)}
                  data-testid={`cashbook-dest-${opt.v}`}
                  className={`h-10 border transition-colors uppercase tracking-wider text-[11px] font-semibold ${dest === opt.v ? (opt.color === "orange" ? "bg-[#F97316] border-[#F97316] text-white" : opt.color === "black" ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "bg-gray-700 border-gray-700 text-white") : "border-gray-300 hover:border-gray-900"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200 mb-6">
          <div className="p-5 border-r border-gray-200 bg-emerald-50">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-800 font-medium">
              <ArrowDown size={12} weight="bold" /> Total Pemasukan
            </div>
            <div className="font-mono-num text-2xl font-bold mt-2 text-emerald-700" data-testid="kpi-in">{formatIDR(t.total_in || 0)}</div>
            <div className="text-[11px] text-emerald-700/70 font-mono-num mt-1">
              DP {formatIDR(t.dp_in || 0)} · Cicilan {formatIDR(t.installment_in || 0)} · Cash {formatIDR(t.cash_sale_in || 0)}
            </div>
          </div>
          <div className="p-5 border-r border-gray-200 bg-rose-50">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-rose-800 font-medium">
              <ArrowUp size={12} weight="bold" /> Total Pengeluaran
            </div>
            <div className="font-mono-num text-2xl font-bold mt-2 text-rose-700" data-testid="kpi-out">{formatIDR(t.total_out || 0)}</div>
            <div className="text-[11px] text-rose-700/70 font-mono-num mt-1">
              Beli {formatIDR(t.purchase_out || 0)} · Komisi {formatIDR(t.commission_out || 0)} · Biaya {formatIDR(t.expense_out || 0)}
            </div>
          </div>
          <div className="p-5" style={{ background: (t.net || 0) >= 0 ? "#F0FDF4" : "#FEF2F2" }}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-700 font-medium">
              <Wallet size={12} weight="bold" /> Saldo Bersih
            </div>
            <div className="font-mono-num text-2xl font-bold mt-2" style={{ color: (t.net || 0) >= 0 ? "#10B981" : "#E63946" }} data-testid="kpi-net">
              {formatIDR(t.net || 0)}
            </div>
            <div className="text-[11px] text-gray-500 font-mono-num mt-1">
              <TrendUp size={10} className="inline mr-1" /> {(data.days || []).length} hari tercatat
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-3">Tanggal</th>
                <th className="p-3 text-right">DP</th>
                <th className="p-3 text-right">Cicilan</th>
                <th className="p-3 text-right">Cash Sale</th>
                <th className="p-3 text-right bg-emerald-50 text-emerald-800">Total Masuk</th>
                <th className="p-3 text-right">Pembelian</th>
                <th className="p-3 text-right">Komisi</th>
                <th className="p-3 text-right">Biaya Mobil</th>
                <th className="p-3 text-right bg-rose-50 text-rose-800">Total Keluar</th>
                <th className="p-3 text-right">Saldo Harian</th>
              </tr>
            </thead>
            <tbody>
              {(data.days || []).map((r) => (
                <tr key={r.date} className="border-b border-gray-100" data-testid={`cashbook-row-${r.date}`}>
                  <td className="p-3 font-mono-num text-xs">{formatDate(r.date)}</td>
                  <td className="p-3 text-right font-mono-num text-xs text-gray-700">{r.dp_in ? formatIDR(r.dp_in) : "-"}</td>
                  <td className="p-3 text-right font-mono-num text-xs text-gray-700">{r.installment_in ? formatIDR(r.installment_in) : "-"}</td>
                  <td className="p-3 text-right font-mono-num text-xs text-gray-700">{r.cash_sale_in ? formatIDR(r.cash_sale_in) : "-"}</td>
                  <td className="p-3 text-right font-mono-num font-semibold bg-emerald-50/40">{formatIDR(r.total_in)}</td>
                  <td className="p-3 text-right font-mono-num text-xs text-gray-700">{r.purchase_out ? formatIDR(r.purchase_out) : "-"}</td>
                  <td className="p-3 text-right font-mono-num text-xs text-gray-700">{r.commission_out ? formatIDR(r.commission_out) : "-"}</td>
                  <td className="p-3 text-right font-mono-num text-xs text-gray-700">{r.expense_out ? formatIDR(r.expense_out) : "-"}</td>
                  <td className="p-3 text-right font-mono-num font-semibold bg-rose-50/40">{formatIDR(r.total_out)}</td>
                  <td className="p-3 text-right font-mono-num font-bold" style={{ color: r.net >= 0 ? "#10B981" : "#E63946" }}>{formatIDR(r.net)}</td>
                </tr>
              ))}
              {(data.days || []).length === 0 && (
                <tr><td colSpan="10" className="p-10 text-center text-sm text-gray-500">{busy ? "Memuat..." : "Belum ada transaksi di bulan ini"}</td></tr>
              )}
            </tbody>
            {(data.days || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-900 bg-yellow-50">
                  <td className="p-3 font-bold uppercase text-xs">Total</td>
                  <td className="p-3 text-right font-mono-num font-bold">{formatIDR(t.dp_in || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold">{formatIDR(t.installment_in || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold">{formatIDR(t.cash_sale_in || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold text-emerald-700">{formatIDR(t.total_in || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold">{formatIDR(t.purchase_out || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold">{formatIDR(t.commission_out || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold">{formatIDR(t.expense_out || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold text-rose-700">{formatIDR(t.total_out || 0)}</td>
                  <td className="p-3 text-right font-mono-num font-bold" style={{ color: (t.net || 0) >= 0 ? "#10B981" : "#E63946" }}>{formatIDR(t.net || 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
